import {AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {ScriptConfigurations} from './script.models';
import {KlaDynamicScriptLoaderService} from '../services/kla.dynamic-script-loader.service';
import {TitleConfigurations, TitleLineConfigurations} from '../title/title.configurations';
import * as Highcharts from 'highcharts';
import { SafeHtmlPipe } from '../safe-html/safe-html';
import { CommonModule } from '@angular/common';
import { TitleComponent } from '../title/title.component';
import { BadgesComponent } from '../badges/badges.component';

// declare var require: any;
// const Highcharts = require('highcharts');
// require('highcharts/modules/exporting')(Highcharts);

@Component({
  selector: 'kla-script',
  templateUrl: './script.component.html',
  styleUrls: ['./script.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SafeHtmlPipe,
    TitleComponent,
    BadgesComponent
  ]
})
export class ScriptComponent implements OnInit, AfterViewInit {

  @ViewChild('iframe', {static: false}) iframe: ElementRef;
  iframeHeight = 0;
  iframeDisplay = true;
  html;
  styles;
  js;
  doc;

  @Input()
  public indicatorTitle: string;
  @Input()
  public config: ScriptConfigurations;

  @ViewChild('division', {static: true})
  public division: ElementRef;

  @Input()
  public lineConfig: TitleLineConfigurations;
  public dynamicLineConfig: TitleLineConfigurations = {
    sectionColor: 'red',
    sectionIndex: 1,
    numberOfSections: 0,
    menuIndex: 0
  };

  public dynamicTitleConfig: TitleConfigurations = {header: '', title: '', description: '', footer: '', color: '', badges:[]};

  public loadDynamicHtml = false;

  constructor(
    private dynamicScriptLoader: KlaDynamicScriptLoaderService
  ) {
  }

  onLoad(iframe) {
    this.doc = iframe.contentDocument || iframe.contentWindow;
    const el = this.doc.createElement('div');
    if (this.styles) {
      for (const item of this.styles) {
        if (item) {
          el.innerHTML += item;
        }
      }
    }
    if (this.html) {
      for (const item of this.html) {
        if (item.html) {
          el.innerHTML += item.html;
        }
      }
    }
    if (this.js) {
      for (const item of this.js) {
        if (item.javascript) {
          this.includeIframeScripts(item);
        }
      }
    }
    if (el.children && el.children.length) {
      this.doc.body.appendChild(el);
    } else {
      this.iframeDisplay = false;
    }
    setTimeout(() => {
      this.iframeHeight = this.doc.body.scrollHeight;
    }, 5000);
  }

  includeIframeScripts(jsItem) {
    const re: RegExp = /<script\b[^>]*>([\s\S]*?)<\/script>/gm;
    let match: any;
    const scripts = [];
    let javascriptWithoutTags = jsItem.javascript;
    while (match = re.exec(jsItem.javascript)) {
      scripts.push(match[0]);
      javascriptWithoutTags = javascriptWithoutTags.replace(match[0], '');
    }
    scripts.forEach(script => {
      const src = this.getSrcFromScript(script);
      if (src) {
        const el = this.doc.createElement('script');
        el.src = src;
        this.doc.head.appendChild(el);
      } else {
        const insideScript = this.removeScriptTags(script);
        const el = this.doc.createElement('script');
        el.innerHTML = insideScript;
        this.doc.head.appendChild(el);
      }
    });
    this.executeScriptFromString(javascriptWithoutTags);
  }

  ngOnInit() {
    if (this.config['configurations']) { // action-plan page recieves config.configurations{} instead of config{}
      this.config = this.config['configurations'];
    }
    this.html = this.getHtml();
    this.getStyles();
    this.js = this.includeScripts();
  }

  ngAfterViewInit() {
    this.includeDynamicHtml();
  }

  private includeScripts() {
    if (this.config.iframeStatus) {
      if (!Array.isArray(this.config.javascript)) {
        // @ts-ignore
        const hasScriptTags: boolean = this.config.javascript.includes('<script') && this.config.javascript.includes('</script>');

        if (hasScriptTags) {
          return [{javascript: this.config.javascript}];
        } else {
          return [{javascript: '<script type="text/javascript">' + this.config.javascript + '</script>'}];
        }
      } else {
        for (const item of this.config.javascript) {
          const hasScriptTags: boolean = item.javascript.includes('<script') && item.javascript.includes('</script>');
          if (!hasScriptTags) {
            item.javascript = '<script type="text/javascript">' + item.javascript + '</script>';
          }
        }
        return this.config.javascript;
      }
    } else {
      if (this.config.javascript) {
        if (!Array.isArray(this.config.javascript)) {
          // @ts-ignore
          const hasScriptTags: boolean = this.config.javascript.includes('<script') && this.config.javascript.includes('</script>');
          if (hasScriptTags) {
            this.loadFromScriptTags(this.config.javascript);
          } else {
            // @ts-ignore
            if (this.config.javascript.includes('Highcharts.chart(')) {
              this.executeScriptFromString(this.config.javascript);
            } else {
              const js = document.createElement('script');
              js.innerHTML = this.config.javascript;
              this.division.nativeElement.appendChild(js);
            }
          }
        } else {
          for (const jsItem of this.config.javascript) {
            const hasScriptTags: boolean = jsItem.javascript.includes('<script') && jsItem.javascript.includes('</script>');
            if (hasScriptTags) {
              this.loadFromScriptTags(jsItem);
            } else {
              if (jsItem.javascript.includes('Highcharts.chart(')) {
                this.executeScriptFromString(jsItem.javascript);
              } else {
                const js = document.createElement('script');
                js.innerHTML = jsItem.javascript;
                this.division.nativeElement.appendChild(js);
              }
            }
          }
        }
      }
    }
  }

  private loadFromScriptTags(jsItem) {
    const re: RegExp = /<script\b[^>]*>([\s\S]*?)<\/script>/gm;
    let match: any;
    const scripts = [];
    let javascriptWithoutTags = jsItem.javascript;
    while (match = re.exec(jsItem.javascript)) {
      scripts.push(match[0]);
      javascriptWithoutTags = javascriptWithoutTags.replace(match[0], '');
    }
    scripts.forEach(script => {
      const src = this.getSrcFromScript(script);
      if (src) {
        this.loadHeadScript(src);
      } else {
        const insideScript = this.removeScriptTags(script);
        this.executeScriptFromString(insideScript);
      }
    });
    this.executeScriptFromString(javascriptWithoutTags);
  }

  private executeScriptFromString(insideScript: any) {
    const failedExecuteAttempts = 0;
    this.attemptToExecuteScriptFromString(insideScript, failedExecuteAttempts);
  }

  private attemptToExecuteScriptFromString(insideScript: any, failedExecuteAttempts: number) {
    if (!insideScript?.trim()) {
      return false;
    }
    try {
      if (insideScript.includes('Highcharts.chart')) {
        if (failedExecuteAttempts == 0 || !!(failedExecuteAttempts && !(failedExecuteAttempts % 2))) {
          const remainingScript = insideScript.split('Highcharts.chart').pop();
          const renderTo = remainingScript.substring(2, remainingScript.indexOf(',') - 1);
          const rawOptions = remainingScript.substring((remainingScript.indexOf('{') - 1), remainingScript.indexOf(');'));
          let jsonOptions = rawOptions.replace(/(['"])?([a-zA-Z0-9_]+)(['"])?:/g, '"$2": ').replace(/'/g, '"');
          if (jsonOptions.charAt(0) == ',') {
            jsonOptions = jsonOptions.substring(1);
          }
          Highcharts.chart(renderTo, JSON.parse(jsonOptions));
        } else {
          eval(insideScript);
        }
      } else {
        const el = document.createElement('script');
        el.innerHTML = insideScript;
        document.head.appendChild(el);
      }
    } catch (error) {
      const maxAttempts = 6;
      if (failedExecuteAttempts < maxAttempts) {
        failedExecuteAttempts++;
        if (failedExecuteAttempts == maxAttempts) {
          console.error(`Unable to load chart after ${maxAttempts} attemts.`, error);
        } else {
          console.warn(`Failed to load chart. Trying again...(${failedExecuteAttempts})`, error);
        }
        setTimeout(() => {
          this.attemptToExecuteScriptFromString(insideScript, failedExecuteAttempts);
        }, 1000);
      }
    }
  }

  private getSrcFromScript(string) {
    const fullSrc = string.split('src=')[1];
    let src: string;
    if (fullSrc) {
      src = fullSrc.split(/[ >]/)[0];
      src = src.substring(1, src.length - 1);
    }
    return src;
  }

  private loadHeadScript(src: string) {
    this.dynamicScriptLoader.load([src]);
  }

  private removeScriptTags(script) {
    return script.replace(/(<\S([^>]+)>)/ig, '');
  }

  getStyles() {
    const styleArray = [];
    if (this.config.css) {
      if (Array.isArray(this.config.css)) {
        for (const item of this.config.css) {
          styleArray.push('<style type="text/css">' + item.css + '</style>');
        }
      } else {
        this.config.css = [{css: this.config.css, title: ''}];
        this.getStyles();
        return;
      }
    }
    this.styles = styleArray;
  }

  getHtml() {
    if (!Array.isArray(this.config.html)) {
      return [{html: this.config.html}];
    } else {
      return this.config.html;
    }
  }

  private includeDynamicHtml() {
    const dots = this.division.nativeElement.querySelector('#dynamic-dots');
    const title = this.division.nativeElement.querySelector('#dynamic-title');
    const description = this.division.nativeElement.querySelector('#dynamic-description');
    if (dots) {
      dots.style.display = 'none';
      this.dynamicLineConfig = this.lineConfig;
      this.dynamicTitleConfig.header = this.indicatorTitle ? this.indicatorTitle : '';
    }
    if (title) {
      title.style.display = 'none';
      this.dynamicTitleConfig.title = title.innerText;
    }
    if (description) {
      description.style.display = 'none';
      this.dynamicTitleConfig.description = description.innerText;
    }
    setTimeout(() => {
      this.loadDynamicHtml = true;
    });
  }
}
